/**
 * Kanalregeln (Board „Vertrieb“): oben die Zwölf-Monats-Leiste — wann ist welcher Kanal zu —,
 * darunter die Regeln mit Klartext-Status und der einen passenden nächsten Aktion. Anlegen und
 * Bearbeiten laufen im Blatt, damit die Leiste beim Arbeiten sichtbar bleibt. Ohne Full lesend.
 */
import { useState } from 'react';
import { Plus } from 'lucide-react';
import {
  Button,
  ConfirmDialog,
  SurfaceLoadError,
  SurfaceLoading,
  SurfaceStaleHint,
} from '@/shared/components';
import { problemText, useApiProblem, type ChannelCloseoutListItem } from '@/shared/api';
import { ChannelCloseoutEditorSheet } from './ChannelCloseoutEditorSheet';
import { ChannelCloseoutList } from './ChannelCloseoutList';
import { ChannelCloseoutTimeline } from './ChannelCloseoutTimeline';
import { channelLabel, listGerman } from './channelCloseoutPresentation';
import { useChannelCloseoutAction } from './useChannelCloseoutAction';
import { useCloseoutTimeline } from './useCloseoutTimeline';
import { CloseoutsOutsideViewNote } from './CloseoutsOutsideViewNote';

export function ChannelCloseoutPanel({
  propertyGroupId,
  canManage,
  today,
  onOpenApartment,
  onShowInCalendar,
}: {
  propertyGroupId: string;
  canManage: boolean;
  today: string;
  onOpenApartment: (propertyId: string, startDate: string, endDate: string) => void;
  onShowInCalendar: (startDate: string) => void;
}) {
  // undefined = Leiste zu, null = neue Regel, sonst die Id der bearbeiteten Regel.
  const [editing, setEditing] = useState<string | null | undefined>(undefined);
  const [removing, setRemoving] = useState<ChannelCloseoutListItem | null>(null);
  const timeline = useCloseoutTimeline(propertyGroupId, today);
  const timelineProblem = useApiProblem(timeline.query.error);
  const action = useChannelCloseoutAction(propertyGroupId);
  const shown = timeline.query.data?.items ?? [];
  const hidden = (timeline.query.data?.total ?? 0) - shown.length;
  const retryTimeline = () => void timeline.query.refetch();

  return (
    <section
      aria-label="Kanalregeln"
      className="bg-card border-hairline rounded-tile shadow-float flex flex-col gap-4 border px-[22px] py-5"
    >
      <div className="flex flex-wrap items-center gap-2.5">
        <div className="flex min-w-0 grow flex-col">
          <h2 className="text-ink font-serif text-[22px] font-bold">Kanalregeln</h2>
          <p className="text-ink-soft text-[13px]">
            Schließt Booking.com oder Airbnb für einen Zeitraum, ohne die Preise anzufassen.
          </p>
        </div>
        {canManage && (
          <Button
            variant="brand"
            className="rounded-pill h-11 px-[18px]"
            leftIcon={<Plus size={15} aria-hidden />}
            onClick={() => setEditing(null)}
          >
            Neue Kanalregel
          </Button>
        )}
      </div>
      <div className="space-y-2">
        {/* Leere Spuren hießen „alles offen“ — ein Ladefehler darf nicht so aussehen. */}
        {timeline.query.data && timeline.query.isError && (
          <SurfaceStaleHint onRetry={retryTimeline} retrying={timeline.query.isFetching} />
        )}
        {!timeline.query.data && timeline.query.isError ? (
          <SurfaceLoadError
            message={problemText(timelineProblem, 'Die Zeitleiste konnte nicht geladen werden.')}
            onRetry={retryTimeline}
            retrying={timeline.query.isFetching}
          />
        ) : !timeline.query.data ? (
          <SurfaceLoading label="Zeitleiste wird geladen" rows={2} />
        ) : (
          <ChannelCloseoutTimeline
            window={timeline.window}
            rules={shown}
            today={today}
            canShiftBack={timeline.canShiftBack}
            onShift={timeline.shift}
            onSelect={canManage ? setEditing : undefined}
          />
        )}
        <CloseoutsOutsideViewNote count={timeline.query.data?.outsideViewCount} />
        {hidden > 0 && (
          <p className="text-ink-soft text-xs">
            Die Zeitleiste zeigt {shown.length} Regeln; {hidden} weitere stehen in der Liste.
          </p>
        )}
      </div>
      <ChannelCloseoutList
        propertyGroupId={propertyGroupId}
        canManage={canManage}
        busy={action.isPending}
        onEdit={setEditing}
        onShowInCalendar={onShowInCalendar}
        onAction={(item, next) => {
          // Aufheben öffnet Kanäle wieder — das bestätigt man, statt es zu verklicken.
          if (next === 'remove') setRemoving(item);
          else action.mutate({ item, action: next });
        }}
      />
      {editing !== undefined && (
        <ChannelCloseoutEditorSheet
          propertyGroupId={propertyGroupId}
          closeoutId={editing}
          today={today}
          onClose={() => setEditing(undefined)}
          onOpenApartment={onOpenApartment}
        />
      )}
      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => !open && setRemoving(null)}
        title="Kanalregel aufheben?"
        description={removing && removeText(removing)}
        confirmLabel="Aufheben"
        loading={action.isPending}
        onConfirm={() => {
          if (!removing) return;
          action.mutate(
            { item: removing, action: 'remove' },
            { onSuccess: () => setRemoving(null) },
          );
        }}
      />
    </section>
  );
}

function removeText(item: ChannelCloseoutListItem) {
  const channels = item.channels.map(channelLabel);
  return (
    `„${item.name}“ wird bei Channex entfernt. ${listGerman(channels)} ` +
    `${channels.length > 1 ? 'öffnen' : 'öffnet'} danach wieder, sofern keine andere Regel schließt.`
  );
}
